import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import JSZip from "jszip";
import {
	CheckIcon,
	DownloadIcon,
	FileIcon,
	FileTextIcon,
	FolderOpenIcon,
	ImageIcon,
	LoaderIcon,
	MusicIcon,
	UploadIcon,
	VideoIcon,
	XIcon,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";

import { AudioCard } from "@/components/resources/audio-card";
import { GenericCard } from "@/components/resources/generic-card";
import { ImageCard } from "@/components/resources/image-card";
import { VideoCard } from "@/components/resources/video-card";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { cn } from "@/components/ui/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { filesInfiniteQueryOptions } from "@/hooks/query/file";
import { useUploadMutipleFiles } from "@/hooks/use-upload-file";
import { useSession } from "@/lib/auth-client";
import type { FileItem, FileTypeFilter } from "@/lib/file-utils";
import { filterToMimeTypes } from "@/lib/file-utils";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";
import { $createBulkDownloadUrls } from "@/server/actions/file";

export const Route = createFileRoute("/(app)/resources/")({
	component: ResourcesPage,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.sidebar_resources()} | StudyOS`,
			description:
				"Manage uploaded files, images, audio, and video assets in your StudyOS workspace.",
			pathname: match.pathname,
		}),
});

function ResourcesPage() {
	const { data: session } = useSession();
	const [filter, setFilter] = useState<FileTypeFilter>("all");
	const [selectMode, setSelectMode] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { mutate: uploadFiles, isPending: isUploading } = useUploadMutipleFiles();

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery(
		filesInfiniteQueryOptions({
			pageSize: 12,
			fileType: filterToMimeTypes[filter],
			enabled: !!session,
		}),
	);

	const files = data?.pages.flatMap((page) => page.data) ?? [];
	const filesRef = useRef(files);
	filesRef.current = files;

	const filters: {
		key: FileTypeFilter;
		label: string;
		icon: React.ElementType;
	}[] = [
		{ key: "all", label: m.sidebar_allFiles(), icon: FileIcon },
		{ key: "image", label: m.sidebar_images(), icon: ImageIcon },
		{ key: "audio", label: m.sidebar_audio(), icon: MusicIcon },
		{ key: "video", label: m.sidebar_video(), icon: VideoIcon },
	];

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = e.target.files;
		if (!selectedFiles || selectedFiles.length === 0) return;
		uploadFiles(Array.from(selectedFiles));
		e.target.value = "";
	};

	const toggleSelection = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const toggleSelectMode = useCallback(() => {
		setSelectMode((prev) => {
			if (prev) setSelectedIds(new Set());
			return !prev;
		});
	}, []);

	const selectAll = useCallback(() => {
		setSelectedIds(new Set(filesRef.current.map((f) => f.id)));
	}, []);

	const { mutate: zipDownload, isPending: isZipping } = useMutation({
		mutationFn: (selected: { fileUrl: string; fileName: string }[]) =>
			$createBulkDownloadUrls({ data: { files: selected } }),
		onSuccess: async (signedUrls) => {
			const zip = new JSZip();
			const nameCounts = new Map<string, number>();

			await Promise.all(
				signedUrls.map(async ({ fileName, downloadUrl }) => {
					const response = await fetch(downloadUrl);
					const blob = await response.blob();

					const count = nameCounts.get(fileName) ?? 0;
					nameCounts.set(fileName, count + 1);
					const uniqueName =
						count > 0
							? fileName.replace(/(\.[^.]+)$/, `_${count.toString()}$1`)
							: fileName;

					zip.file(uniqueName, blob);
				}),
			);

			const content = await zip.generateAsync({ type: "blob" });
			const url = URL.createObjectURL(content);
			const a = document.createElement("a");
			a.href = url;
			a.download = `studyos-resources-${Date.now().toString()}.zip`;
			a.click();
			URL.revokeObjectURL(url);

			setSelectedIds(new Set());
			setSelectMode(false);
		},
	});

	const handleZipDownload = useCallback(() => {
		const selected = filesRef.current.filter((f) => selectedIds.has(f.id));
		if (selected.length === 0) return;
		zipDownload(selected.map((f) => ({ fileUrl: f.url, fileName: f.name })));
	}, [selectedIds, zipDownload]);

	return (
		<div className="flex h-full flex-col">
			{/* Sticky header + filters */}
			<div className="shrink-0 space-y-4 border-b bg-background px-6 pt-6 pb-4">
				<div className="mx-auto flex w-full max-w-5xl items-center gap-3">
					<FolderOpenIcon className="size-6 text-muted-foreground" />
					<h1 className="flex-1 text-2xl font-semibold">{m.sidebar_resources()}</h1>
					<input
						ref={fileInputRef}
						type="file"
						multiple
						className="hidden"
						accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.pptx"
						onChange={handleFileChange}
					/>
					{files.length > 0 && (
						<Button
							variant={selectMode ? "default" : "outline"}
							size="sm"
							onClick={toggleSelectMode}
						>
							{selectMode ? (
								<>
									<XIcon className="size-4" />
									{m.common_cancel()}
								</>
							) : (
								<>
									<CheckIcon className="size-4" />
									{m.common_select()}
								</>
							)}
						</Button>
					)}
					<Button
						variant="outline"
						size="sm"
						disabled={isUploading}
						onClick={() => fileInputRef.current?.click()}
					>
						{isUploading ? (
							<LoaderIcon className="size-4 animate-spin" />
						) : (
							<UploadIcon className="size-4" />
						)}
						{m.common_upload()}
					</Button>
				</div>

				<div className="mx-auto flex w-full max-w-5xl gap-2">
					{filters.map((f) => (
						<button
							key={f.key}
							type="button"
							onClick={() => setFilter(f.key)}
							className={cn(
								"inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
								filter === f.key
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:bg-muted/80",
							)}
						>
							<f.icon className="size-3.5" />
							{f.label}
						</button>
					))}
				</div>
			</div>

			{/* Scrollable content */}
			<div className="flex-1 overflow-y-auto p-6">
				<div className="mx-auto w-full max-w-5xl space-y-6">
					{isPending ? (
						<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
							{Array.from({ length: 8 }).map((_, i) => (
								<Skeleton
									key={`skeleton-${i.toString()}`}
									className="aspect-square rounded-lg"
								/>
							))}
						</div>
					) : files.length === 0 ? (
						<Empty className="py-16">
							<EmptyMedia variant="icon">
								<FileTextIcon className="size-8" />
							</EmptyMedia>
							<EmptyDescription>{m.sidebar_noFiles()}</EmptyDescription>
						</Empty>
					) : (
						<>
							{filter === "image" || filter === "all" ? (
								<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
									{files.map((file) => (
										<SelectableFileCard
											key={file.id}
											file={file}
											selectMode={selectMode}
											selected={selectedIds.has(file.id)}
											onToggle={toggleSelection}
										/>
									))}
								</div>
							) : filter === "audio" ? (
								<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
									{files.map((file) => (
										<SelectableFileCard
											key={file.id}
											file={file}
											selectMode={selectMode}
											selected={selectedIds.has(file.id)}
											onToggle={toggleSelection}
										/>
									))}
								</div>
							) : (
								<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
									{files.map((file) => (
										<SelectableFileCard
											key={file.id}
											file={file}
											selectMode={selectMode}
											selected={selectedIds.has(file.id)}
											onToggle={toggleSelection}
										/>
									))}
								</div>
							)}

							{hasNextPage && (
								<div className="flex justify-center pt-4">
									<Button
										variant="outline"
										onClick={() => fetchNextPage()}
										disabled={isFetchingNextPage}
									>
										{isFetchingNextPage
											? m.common_loading()
											: m.common_loadMore()}
									</Button>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* Selection action bar */}
			{selectMode && selectedIds.size > 0 && (
				<SelectionActionBar
					selectedCount={selectedIds.size}
					totalCount={files.length}
					isZipping={isZipping}
					onSelectAll={selectAll}
					onClearSelection={() => setSelectedIds(new Set())}
					onDownloadZip={handleZipDownload}
				/>
			)}
		</div>
	);
}

function FileCardContent({ file }: { file: FileItem }) {
	if (file.fileType.startsWith("image/")) return <ImageCard file={file} />;
	if (file.fileType.startsWith("audio/")) return <AudioCard file={file} />;
	if (file.fileType.startsWith("video/")) return <VideoCard file={file} />;
	return <GenericCard file={file} />;
}

function SelectableFileCard({
	file,
	selectMode,
	selected,
	onToggle,
}: {
	file: FileItem;
	selectMode: boolean;
	selected: boolean;
	onToggle: (id: string) => void;
}) {
	if (!selectMode) {
		return <FileCardContent file={file} />;
	}

	return (
		<button
			type="button"
			onClick={() => onToggle(file.id)}
			className={cn(
				"group/select relative cursor-pointer rounded-lg text-left transition-all [&>div]:rounded-lg [&>div]:border-0 [&>div]:shadow-none [&>div]:hover\\:shadow-none",
				selected ? "ring-primary ring-2" : "ring-1 ring-border hover:ring-foreground/20",
			)}
		>
			{/* Selection indicator */}
			<div
				className={cn(
					"absolute top-1.5 right-1.5 z-10 flex size-5 items-center justify-center rounded-full border transition-colors",
					selected
						? "border-primary bg-primary text-primary-foreground"
						: "border-muted-foreground/40 bg-background/80 opacity-0 group-hover/select:opacity-100",
				)}
			>
				{selected && <CheckIcon className="size-3" />}
			</div>

			<div className="pointer-events-none">
				<FileCardContent file={file} />
			</div>
		</button>
	);
}

function SelectionActionBar({
	selectedCount,
	totalCount,
	isZipping,
	onSelectAll,
	onClearSelection,
	onDownloadZip,
}: {
	selectedCount: number;
	totalCount: number;
	isZipping: boolean;
	onSelectAll: () => void;
	onClearSelection: () => void;
	onDownloadZip: () => void;
}) {
	return (
		<div className="shrink-0 border-t bg-background px-6 py-3">
			<div className="mx-auto flex w-full max-w-5xl items-center gap-3">
				<span className="text-sm text-muted-foreground">
					{selectedCount} / {totalCount} selected
				</span>
				<Button variant="ghost" size="sm" onClick={onSelectAll}>
					{m.common_selectAll()}
				</Button>
				<Button variant="ghost" size="sm" onClick={onClearSelection}>
					{m.common_clearSelection()}
				</Button>
				<div className="flex-1" />
				<Button size="sm" disabled={isZipping} onClick={onDownloadZip}>
					{isZipping ? (
						<LoaderIcon className="size-4 animate-spin" />
					) : (
						<DownloadIcon className="size-4" />
					)}
					{isZipping ? m.common_loading() : m.common_downloadZip()}
				</Button>
			</div>
		</div>
	);
}
